import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AdminSurfaceConfig } from './surface';
import {
  isActiveAdminProfile,
  type AuthUser,
  type AdminProfile,
} from './lib/auth';
import { apiRequest, hasApiConfig } from './lib/api';

type AuthStatus = 'config_error' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  profile: AdminProfile | null;
  configError: string | null;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  signIn: (identifier: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  surfaceConfig: AdminSurfaceConfig;
}

interface CloudflareAdminUser {
  id: string;
  role: AdminProfile['role'];
  nickname: string | null;
  username: string | null;
  status: AdminProfile['status'];
  store_id: string | null;
  franchise_id: string | null;
}

function toProfile(user: CloudflareAdminUser): AdminProfile {
  return {
    id: user.id,
    role: user.role,
    nickname: user.nickname,
    internal_login_id: user.username,
    phone_number: null,
    status: user.status,
  };
}

export function AuthProvider({ children, surfaceConfig }: AuthProviderProps) {
  const configured = hasApiConfig();
  const [status, setStatus] = useState<AuthStatus>(configured ? 'loading' : 'config_error');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const configError = configured ? null : 'VITE_API_BASE_URL이 설정되지 않았습니다.';

  const clearAuthState = useCallback(() => {
    setUser(null);
    setProfile(null);
    setStatus(configured ? 'unauthenticated' : 'config_error');
  }, [configured]);

  const applyUser = useCallback(
    (nextUser: CloudflareAdminUser) => {
      const nextProfile = toProfile(nextUser);
      if (!isActiveAdminProfile(nextProfile, surfaceConfig)) {
        clearAuthState();
        return false;
      }
      setUser({ id: nextUser.id, email: null });
      setProfile(nextProfile);
      setStatus('authenticated');
      return true;
    },
    [clearAuthState, surfaceConfig],
  );

  useEffect(() => {
    if (!configured) return;
    let mounted = true;
    void apiRequest<{ user: CloudflareAdminUser }>('/api/v1/auth/me')
      .then(({ user: nextUser }) => {
        if (mounted) applyUser(nextUser);
      })
      .catch(() => {
        if (mounted) clearAuthState();
      });
    return () => {
      mounted = false;
    };
  }, [applyUser, clearAuthState, configured]);

  const signIn = useCallback(
    async (identifier: string, password: string) => {
      if (!configured) throw new Error(configError ?? 'Cloudflare API 설정이 없습니다.');
      setStatus('loading');
      try {
        const { user: nextUser } = await apiRequest<{ user: CloudflareAdminUser }>(
          '/api/v1/auth/admin/login',
          {
            method: 'POST',
            body: JSON.stringify({ identifier, password, surface: surfaceConfig.surface }),
          },
        );
        if (!applyUser(nextUser)) {
          await apiRequest<void>('/api/v1/auth/logout', { method: 'POST' });
          throw new Error('이 관리자 페이지에 접근할 수 없는 계정입니다.');
        }
      } catch (error) {
        clearAuthState();
        throw error;
      }
    },
    [applyUser, clearAuthState, configError, configured, surfaceConfig.surface],
  );

  const signOut = useCallback(async () => {
    if (configured) {
      await apiRequest<void>('/api/v1/auth/logout', { method: 'POST' }).catch(() => undefined);
    }
    clearAuthState();
  }, [clearAuthState, configured]);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await apiRequest<{ ok: true }>('/api/v1/auth/password/change', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, profile, configError, changePassword, signIn, signOut }),
    [changePassword, configError, profile, signIn, signOut, status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider.');
  return value;
}
